import type { AnalyticsConsent } from './analyticsConsent'
import { redactSensitiveAnalyticsUrl } from './analyticsUrl.ts'
import { clearGoogleAnalyticsCookies } from './googleAnalyticsCookies.ts'

export const GA_MEASUREMENT_ID = 'G-Q063QBRZT0'

type GoogleTag = (
  command: 'event' | 'config' | 'js' | 'set' | 'consent',
  eventNameOrId: string | Date,
  params?: Record<string, unknown>
) => void

export interface GoogleAnalyticsTarget {
  dataLayer?: unknown[]
  gtag?: GoogleTag
  __microplasticsGaConfigured?: boolean
  location?: { href?: string }
  document?: { cookie: string }
  [key: string]: unknown
}

function getOrCreateGoogleTag(target: GoogleAnalyticsTarget): GoogleTag {
  if (target.gtag) return target.gtag

  const queue = (target.dataLayer = target.dataLayer || [])
  const queueGtagCall = function () {
    // GA's official snippet queues the Arguments object, rather than an Array.
    // eslint-disable-next-line prefer-rest-params
    queue.push(arguments)
  } as GoogleTag
  target.gtag = queueGtagCall
  return queueGtagCall
}

/**
 * Reconcile GA's independent persisted/runtime state with our consent choice.
 * This function never loads the remote library; the consent-gated component
 * does that only after a grant.
 */
export function reconcileGoogleAnalyticsConsent(
  consent: AnalyticsConsent,
  target: GoogleAnalyticsTarget,
  measurementId = GA_MEASUREMENT_ID
): boolean {
  target[`ga-disable-${measurementId}`] = consent !== 'granted'

  if (consent === 'denied') {
    target.gtag?.('consent', 'update', { analytics_storage: 'denied' })
    clearGoogleAnalyticsCookies(target.document, target.location?.href, measurementId)
    return false
  }

  const gtag = getOrCreateGoogleTag(target)
  gtag('consent', target.__microplasticsGaConfigured ? 'update' : 'default', {
    analytics_storage: 'granted',
  })

  if (!target.__microplasticsGaConfigured) {
    gtag('js', new Date())
    const currentUrl = target.location?.href
    gtag('config', measurementId, currentUrl
      ? { page_location: redactSensitiveAnalyticsUrl(currentUrl) }
      : undefined)
    target.__microplasticsGaConfigured = true
  }

  return true
}

export function sendGoogleAnalyticsEvent(
  target: GoogleAnalyticsTarget,
  name: string,
  properties: Record<string, unknown>,
  measurementId = GA_MEASUREMENT_ID
): void {
  // This is called only through the shared explicit-consent gate. Reconcile a
  // first or newly re-granted event so it queues after GA's config, without
  // adding a redundant Consent Mode update before every later event.
  if (
    !target.__microplasticsGaConfigured ||
    target[`ga-disable-${measurementId}`] !== false
  ) {
    reconcileGoogleAnalyticsConsent('granted', target, measurementId)
  }
  target.gtag?.('event', name, {
    ...properties,
    ...(target.location?.href
      ? { page_location: redactSensitiveAnalyticsUrl(target.location.href) }
      : {}),
  })
}
